import React, { useMemo } from "react";
import size from "lodash/size";

import { Box, Card, Typography, Divider } from "@mui/material";

import { grayDark } from "design-system/colors";
import {
  BriefcaseIcon,
  PageViewIcon,
  SeatIcon,
  USBIcon,
  VehicleIcon,
} from "design-system/icons";
import MoovsIconPanel from "../../../globals/MoovsIconPanel";
import { useScreenSize } from "globals/hooks";
import { VehicleCategory } from "types";

type VehicleCategoryCardProps = {
  vehicleCategory: VehicleCategory;
  isNameAndCapacityOnly?: boolean;
  isSelectedVehicle?: boolean;
};

function VehicleCategoryCard(props: VehicleCategoryCardProps) {
  const { vehicleCategory, isNameAndCapacityOnly, isSelectedVehicle } = props;

  // hooks
  const { isMobileView } = useScreenSize();

  // derived state
  const vehiclePic = vehicleCategory?.photos?.[0]?.url;

  const vehicleFeaturesByCategory = useMemo(() => {
    const features = vehicleCategory?.features || [];

    return {
      general: features
        .filter(({ category }) => category === "general")
        .map(({ name }) => name),
      multimedia: features
        .filter(({ category }) => category === "multimedia")
        .map(({ name }) => name),
      policies: features
        .filter(({ category }) => category === "policies")
        .map(({ name }) => name),
    };
  }, [vehicleCategory?.features]);

  return (
    <Card
      variant="outlined"
      {...(isSelectedVehicle && {
        style: {
          backgroundColor: "#E3F2FD",
          border: `2px solid #2196F3`,
          maxWidth: "552px",
        },
      })}
    >
      <Box display="flex" flexDirection="column" my={2} mx={3}>
        {!isNameAndCapacityOnly && (
          <Box mb={3}>
            <Typography variant="h3">Vehicle Category</Typography>
          </Box>
        )}
        <Box
          display="flex"
          flexDirection={isMobileView ? "column" : "row"}
          justifyContent="space-between"
        >
          <Box display="flex" flexDirection="row">
            {vehiclePic && (
              <Box
                component="img"
                mr={2}
                sx={{
                  borderRadius: 1,
                  width: "80px",
                  height: "60px",
                  objectFit: "cover",
                }}
                alt={vehicleCategory?.name}
                src={vehiclePic}
              />
            )}
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              height="60px"
            >
              <Box mb={1}>
                <Typography variant="body2" style={{ fontWeight: 700 }}>
                  {vehicleCategory?.name}
                </Typography>
              </Box>
              {isNameAndCapacityOnly && (
                <Box display="flex" alignItems="center">
                  <Typography variant="caption">
                    {`Category . ${vehicleCategory?.capacity} ${
                      vehicleCategory.capacity === 1 ? "person" : "persons"
                    }`}
                  </Typography>
                </Box>
              )}
              {!isNameAndCapacityOnly && (
                <Box display="flex" flexDirection="row">
                  <Box display="flex" flexDirection="row" alignItems="center">
                    <VehicleIcon />
                    <Typography
                      variant="caption"
                      style={{ marginLeft: "10px" }}
                    >
                      Category
                    </Typography>
                  </Box>

                  <Box mx={2}>
                    <Divider orientation="vertical" />
                  </Box>

                  <Box display="flex" flexDirection="row" alignItems="center">
                    <SeatIcon />
                    <Typography
                      variant="caption"
                      style={{ marginLeft: "10px" }}
                    >
                      {vehicleCategory?.capacity}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          {!isNameAndCapacityOnly && (
            <Box
              display="flex"
              flexDirection={isMobileView ? "row" : "column"}
              alignItems={isMobileView ? "center" : "flex-end"}
              {...(isMobileView && { marginTop: 2 })}
            >
              <Box
                display="flex"
                justifyContent="flex-end"
                width={80}
                {...(isMobileView
                  ? { marginRight: "10px" }
                  : { marginBottom: 1 })}
              >
                <Typography variant="overline">FEATURES</Typography>
              </Box>
              <MoovsIconPanel
                centerAlignDrawerText={false}
                bottomDrawerHeader="Vehicle Category Features"
                items={[
                  {
                    mainText: "General",
                    subText: vehicleFeaturesByCategory.general,
                    icon: <BriefcaseIcon color={grayDark} />,
                    renderItem: !!size(vehicleFeaturesByCategory?.general),
                  },
                  {
                    mainText: "Multimedia",
                    subText: vehicleFeaturesByCategory.multimedia,
                    icon: <USBIcon color={grayDark} />,
                    renderItem: !!size(vehicleFeaturesByCategory?.multimedia),
                  },
                  {
                    mainText: "Policies",
                    subText: vehicleFeaturesByCategory.policies,
                    icon: <PageViewIcon color={grayDark} />,
                    renderItem: !!size(vehicleFeaturesByCategory?.policies),
                  },
                ]}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
}

export default VehicleCategoryCard;